import { Component, createRef } from "react";
import image from "../Assets/img/reputation/old-reviews.jpg";

const ANNOTATIONS = [
  {
    id: 1772410610473,
    label: "Confusing workflow",
    description:
      "Clients had trouble finding reviews and there was no sort, filter, or searchbar to find them",
    pointX: 66.69172932330827,
    pointY: 11.707510338167442,
    side: "right",
  },
  {
    id: 1772410682272,
    label: "Inconsistent actions and content",
    description:
      "Depending on what the source was, the action buttons and car hierarchy would change and cause confusion",
    pointX: 90.89219330855019,
    pointY: 42.8097740141815,
    side: "right",
  },
  {
    id: 1772410773405,
    label: "Difficult scannability",
    description:
      "Because the reviews were presented as cards, the scan-ability of the reviews was difficult and would cause more scrolling to see all reviews",
    pointX: 93.21561338289963,
    pointY: 66.57756465847065,
    side: "right",
  },
];

const IMAGE_SRC = image;

const STYLES = `
.aiv-root { width: 100%; box-sizing: border-box; }
.aiv-root * { box-sizing: border-box; }
.aiv-canvas { position: relative; width: 100%; }
.aiv-svg { position: absolute; top: 0; left: 0; width: 100%; height: 100%; pointer-events: none; overflow: visible; }
.aiv-img { display: block; width: 100%; border-radius: 10px; user-select: none; }
.aiv-dot { cursor: pointer; pointer-events: all; }
.aiv-layer { position: absolute; top: 0; left: 0; width: 100%; height: 100%; pointer-events: none; }
.aiv-card {
  position: absolute; pointer-events: all;
  background: #fff; border: 1.5px solid #e8e3dc; border-radius: 10px;
  padding: 12px 14px; width: 220px;
  box-shadow: 0 4px 18px rgba(0,0,0,0.08);
  cursor: pointer; transition: box-shadow 0.2s, border-color 0.2s, transform 0.15s;
}
.aiv-card:hover, .aiv-card.active { box-shadow: 0 8px 28px rgba(0,0,0,0.14); border-color: #1a1a1a; transform: translateY(-1px); z-index:2;}
.aiv-card h4 { font-size: 14px; margin: 0 0 5px 0; color: #1a1a1a; line-height: 1.3; }
.aiv-card p { font-size: 12px; color: #666; margin: 0; line-height: 1.5; }
.aiv-num { display: inline-flex; align-items: center; justify-content: center; width: 20px; height: 20px; border-radius: 50%; background: #1a1a1a; color: #fff; font-size: 11px; font-weight: 600; margin-bottom: 6px; flex-shrink: 0; }
.aiv-list { display: flex; flex-direction: column; gap: 10px; margin-top: 16px; }
.aiv-list-item { display: flex; align-items: flex-start; gap: 12px; background: #fff; border: 1.5px solid #e8e3dc; border-radius: 10px; padding: 12px 14px; box-shadow: 0 4px 18px rgba(0,0,0,0.08); }
.aiv-list-item .aiv-num { margin-bottom: 0; margin-top: 1px; }
.aiv-list-item h4 { font-size: 13px; margin: 0 0 4px 0; color: #1a1a1a; line-height: 1.3; }
.aiv-list-item p { font-size: 12px; color: #666; margin: 0; line-height: 1.5; }
`;

class AnnotatedImageViewer extends Component {
  constructor(props) {
    super(props);
    this.imageRef = createRef();
    this.wrapperRef = createRef();
    this.resizeObserver = null;
    this.state = { w: 0, h: 0, activeId: null, isMobile: false };
  }

  componentDidMount() {
    this.resizeObserver = new ResizeObserver(() => { window.requestAnimationFrame(() => this.updateSize()); });
    if (this.wrapperRef.current)
      this.resizeObserver.observe(this.wrapperRef.current);
  }

  componentWillUnmount() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  updateSize = () => {
    const img = this.imageRef.current;
    if (img) this.setState({ w: img.clientWidth, h: img.clientHeight, isMobile: window.innerWidth <= 1024 });
  };

  cardStyle(ann) {
    const { w, h } = this.state;
    if (!w || !h) return { display: "none" };
    const cardH = 120,
      margin = 18;
    const cardW = window.innerWidth < 640 ? 160 : 220;
    let top = (ann.pointY / 100) * h - cardH / 2;
    top = Math.max(4, Math.min(top, h - cardH - 4));
    const left = ann.side === "right" ? w + margin : -cardW - margin;
    return { left, top };
  }

  linePoints(ann) {
    const { w, h } = this.state;
    if (!w || !h) return null;
    const cardH = 120,
      margin = 18;
    const dotX = (ann.pointX / 100) * w;
    const dotY = (ann.pointY / 100) * h;
    let cardTop = dotY - cardH / 2;
    cardTop = Math.max(4, Math.min(cardTop, h - cardH - 4));
    return {
      x1: dotX,
      y1: dotY,
      x2: ann.side === "right" ? w + margin : -margin,
      y2: cardTop + cardH / 2,
    };
  }

  render() {
    const { w, h, activeId, isMobile } = this.state;
    const annotations = this.props.annotations || ANNOTATIONS;
    const imageSrc = this.props.imageSrc || IMAGE_SRC;
    const right = annotations.filter((a) => a.side === "right").length > 0;
    const left = annotations.filter((a) => a.side === "left").length > 0;

    return (
      <div className="aiv-root">
        <style>{`${STYLES}`}</style>
        <div
          style={{
            paddingRight: isMobile ? 0 : right ? 254 : 0,
            paddingLeft: isMobile ? 0 : left ? 254 : 0,
            position: "relative",
          }}
        >
          <div ref={this.wrapperRef} className="aiv-canvas">
            <img
              ref={this.imageRef}
              src={imageSrc}
              alt="Annotated"
              className="aiv-img"
              onLoad={this.updateSize}
              draggable={false}
            />
            <svg
              className="aiv-svg"
              style={{ overflow: "visible" }}
              viewBox={`0 0 ${w} ${h}`}
              preserveAspectRatio="none"
            >
              {!isMobile && annotations.map((ann) => {
                const p = this.linePoints(ann);
                if (!p) return null;
                return (
                  <line
                    key={ann.id}
                    x1={p.x1}
                    y1={p.y1}
                    x2={p.x2}
                    y2={p.y2}
                    stroke={activeId === ann.id ? "#1a1a1a" : "#c0bbaf"}
                    strokeWidth={activeId === ann.id ? 2 : 1.5}
                    strokeDasharray="5,3"
                  />
                );
              })}
              {annotations.map((ann, i) => {
                const cx = (ann.pointX / 100) * w;
                const cy = (ann.pointY / 100) * h;
                const isActive = activeId === ann.id;
                return (
                  <g
                    key={ann.id}
                    className="aiv-dot"
                    onClick={(e) => {
                      e.stopPropagation();
                      this.setState((s) => ({
                        activeId: s.activeId === ann.id ? null : ann.id,
                      }));
                    }}
                  >
                    <circle
                      cx={cx}
                      cy={cy}
                      r={13}
                      fill="none"
                      stroke="#1a1a1a"
                      strokeWidth={isActive ? 2.5 : 0}
                      opacity={0.15}
                      style={{ transition: "stroke-width 0.15s" }}
                    />
                    <circle
                      cx={cx}
                      cy={cy}
                      r={8}
                      fill={isActive ? "#1a1a1a" : "#fff"}
                      stroke="#1a1a1a"
                      strokeWidth={2}
                      style={{ transition: "fill 0.15s" }}
                    />
                    <text
                      x={cx}
                      y={cy + 4}
                      textAnchor="middle"
                      fontSize={10}
                      fontWeight={600}
                      fontFamily="DM Sans, sans-serif"
                      fill={isActive ? "#fff" : "#1a1a1a"}
                      style={{
                        pointerEvents: "none",
                        userSelect: "none",
                        transition: "fill 0.15s",
                      }}
                    >
                      {i + 1}
                    </text>
                  </g>
                );
              })}
            </svg>
            {!isMobile && (
              <div className="aiv-layer">
                {annotations.map((ann, i) => (
                  <div
                    key={ann.id}
                    className={`aiv-card${activeId === ann.id ? " active" : ""}`}
                    style={this.cardStyle(ann)}
                    onClick={(e) => {
                      e.stopPropagation();
                      this.setState((s) => ({
                        activeId: s.activeId === ann.id ? null : ann.id,
                      }));
                    }}
                  >
                    <div className="aiv-num">{i + 1}</div>
                    <h4>{ann.label}</h4>
                    <p>{ann.description}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
          {isMobile && (
            <div className="aiv-list">
              {annotations.map((ann, i) => (
                <div key={ann.id} className="aiv-list-item">
                  <div className="aiv-num">{i + 1}</div>
                  <div>
                    <h4>{ann.label}</h4>
                    <p>{ann.description}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default AnnotatedImageViewer;
